import React, { useState } from 'react';

import { DownloadOutlined } from '@ant-design/icons';
import { schemas } from '@recurrency/core-api-schema';
import { TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { PendingMinMax, PendingMinMaxScope } from '@recurrency/core-api-schema/dist/purchasing/getPendingMinMax';
import { Radio } from 'antd';

import { Button } from 'components/Button';
import { Modal } from 'components/Modal';
import { Table } from 'components/Table';

import { useCoreApi } from 'hooks/useApi';
import { useGlobalApp } from 'hooks/useGlobalApp';

import { hasUserPermission } from 'utils/roleAndTenant';

import { downloadEclipseMinMaxExcel } from './utils';

// core-api caps a page at 10,000; one file has to hold every pending row, so ask for the most it allows
const PENDING_MIN_MAX_LIMIT = 10000;

const columns = [
  { title: 'Item', key: 'item', render: (_: unknown, row: PendingMinMax) => `${row.itemCode} ${row.itemName}` },
  { title: 'Location', dataIndex: 'locationId', key: 'locationId' },
  { title: 'Min', dataIndex: 'minStock', key: 'minStock' },
  { title: 'Max', dataIndex: 'maxStock', key: 'maxStock' },
  { title: 'Saved by', dataIndex: 'exportedByUserName', key: 'exportedByUserName' },
  {
    title: 'Saved at',
    dataIndex: 'exportedAt',
    key: 'exportedAt',
    render: (exportedAt: string) => new Date(exportedAt).toLocaleString(),
  },
];

/**
 * Min/max changes saved in Recurrency that Eclipse has not shown yet. Eclipse imports min/max from a file, so these
 * stay listed until Eclipse shows a different min/max. Downloading does not clear them.
 */
export function PendingMinMaxModal({ onClose }: { onClose: () => void }) {
  const { activeTenant } = useGlobalApp();
  const canDownloadAll = hasUserPermission(activeTenant, TenantUserPermission.AccessPlanningPlanningDownload);
  const [scope, setScope] = useState(PendingMinMaxScope.Mine);
  const { data, isLoading } = useCoreApi(schemas.purchasing.getPendingMinMax, {
    queryParams: { scope, limit: PENDING_MIN_MAX_LIMIT },
  });
  const items = data?.items ?? [];

  return (
    <Modal
      visible
      destroyOnClose
      title="Pending Min/Max Changes"
      width={900}
      onCancel={onClose}
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            disabled={isLoading || items.length === 0}
            onClick={() =>
              downloadEclipseMinMaxExcel(
                items.map((row) => ({
                  itemId: row.itemId,
                  locationId: row.locationId,
                  newMin: row.minStock,
                  newMax: row.maxStock,
                })),
              )
            }
          >
            {scope === PendingMinMaxScope.All ? 'Download all pending' : 'Download my changes'}
          </Button>
        </>
      }
    >
      <p>
        These changes are saved in Recurrency and not yet in Eclipse. Import the downloaded file in Eclipse. A change
        leaves this list once Eclipse shows a different min/max.
      </p>
      {canDownloadAll && (
        <Radio.Group
          value={scope}
          onChange={(ev) => setScope(ev.target.value)}
          options={[
            { label: 'My changes', value: PendingMinMaxScope.Mine },
            { label: 'All pending', value: PendingMinMaxScope.All },
          ]}
          optionType="button"
          style={{ marginBottom: 16 }}
        />
      )}
      <Table
        data={items}
        columns={columns}
        isLoading={isLoading}
        rowKey={(row: PendingMinMax) => `${row.itemId}-${row.locationId}`}
      />
    </Modal>
  );
}
